import { useMemo } from "react";

import { type ConnectorTool, useConnectorOverview } from "@/lib/api";
import { type Language, useLanguage } from "@/lib/i18n/language";
import { isKnownTool, knownToolNames, ownToolTitle, withToolText } from "@/lib/i18n/tools";
import { labelOrder } from "@/lib/table-sort";

/**
 * Every tool's title, the one rule for every page that names a tool by what
 * Claude calls it (a call, an issue, a usage row): the title comes from the
 * catalogue (`GET /mcp/overview`), served or coming soon, in the dashboard's
 * language. The catalogue lists only the tools of the reader's own role, so a
 * tool of another role (a developer's, in an admin's call log) takes its
 * title from the dashboard's own texts (`@/lib/i18n/tools`; in English it
 * shows its name alone). A tool neither knows is one DC Data no longer
 * serves (`<ToolName>` says so), but only once the catalogue answered: while
 * it loads, or when it failed, a tool it does not name shows its name alone.
 *
 *     const titles = useToolTitles();
 *     <ToolName name={call.tool} titles={titles} />
 *     sortRows(rows, sort, (row) => toolLabel(titles, row.tool));
 */
export interface ToolTitles {
  /** Whether the catalogue answered, so a tool nobody knows is no longer served. */
  loaded: boolean;
  /** A tool's title in the dashboard's language; undefined for a tool without one. */
  get: (name: string) => string | undefined;
  /** Whether DC Data still serves or announces a tool, to the reader's role or another. */
  known: (name: string) => boolean;
  /** Every tool it knows: the catalogue's and those of the dashboard's own texts. */
  names: readonly string[];
}

/** The titles of a catalogue's tools; `tools` undefined while it loads or when it failed. */
export function toolTitles(
  tools: readonly ConnectorTool[] | undefined,
  language: Language,
): ToolTitles {
  const titles = new Map(
    withToolText(tools ?? [], language).map((tool) => [tool.name, tool.title]),
  );
  return {
    loaded: tools !== undefined,
    get: (name) => titles.get(name) ?? ownToolTitle(name, language),
    known: (name) => titles.has(name) || isKnownTool(name),
    names: [...new Set([...titles.keys(), ...knownToolNames()])],
  };
}

/** Every tool's title from the catalogue, in the dashboard's language. */
export function useToolTitles(): ToolTitles {
  const language = useLanguage();
  const tools = useConnectorOverview().data?.tools;
  return useMemo(() => toolTitles(tools, language), [tools, language]);
}

/** A tool as words to sort or search on, or to name in a line of facts: its title, else its name. */
export function toolLabel(titles: ToolTitles, name: string): string {
  return titles.get(name) ?? name;
}

/**
 * Tools in the order of the words a table shows for them (`toolLabel`: the
 * title, else the name), for a table the server sorts by tool: every tool the
 * titles know, and `more`, such as the tools of the calls of the past quarter
 * (one DC Data no longer serves sorts by its name among the titles).
 */
export function toolSortOrder(
  titles: ToolTitles,
  more: Iterable<string>,
  language: Language,
): string[] {
  return labelOrder([...titles.names, ...more], (name) => toolLabel(titles, name), language);
}

/**
 * Whether DC Data no longer serves a tool: neither the catalogue nor the
 * dashboard's texts know it (only once the catalogue answered).
 */
export function isRetiredTool(titles: ToolTitles, name: string): boolean {
  return titles.loaded && !titles.known(name);
}
